import dayjs from '@app/utils/dayjs-setup';
import type { ConfigType, Dayjs } from 'dayjs';

// One display zone for the whole dashboard: the browser's own zone (the profile follows it, see AuthProvider).

export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export const isValidTimezone = (tz: unknown): tz is string => {
  if (typeof tz !== 'string' || !tz.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

let current: string = getBrowserTimezone();
dayjs.tz.setDefault(current);

type Listener = (tz: string) => void;
const listeners = new Set<Listener>();

export const getDisplayTimezone = (): string => current;

/** Sets the zone every instant renders in; an invalid or missing zone falls back to the browser's. */
export const setDisplayTimezone = (tz?: string | null): string => {
  const next = isValidTimezone(tz) ? tz : getBrowserTimezone();
  if (next === current) return current;
  current = next;
  dayjs.tz.setDefault(next);
  listeners.forEach((listener) => listener(next));
  return next;
};

export const subscribeDisplayTimezone = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// ISO date or date-time with no offset: a wall-clock value, not an instant.
const ZONELESS_ISO = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/;

/**
 * An instant (ISO with offset, Date, epoch ms, dayjs) seen in the display zone.
 * A zoneless ISO string ("2026-10-29") is read as display-zone wall time, so a date never shifts a day.
 */
export const inDisplayZone = (value?: ConfigType): Dayjs => {
  // Same zone as the browser: plain local dayjs, which keeps DST-correct arithmetic (a tz() one has a fixed offset).
  if (current === getBrowserTimezone()) return dayjs(value);
  if (typeof value === 'string' && ZONELESS_ISO.test(value.trim())) return dayjs.tz(value.trim(), current);
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(current) : parsed;
};

/** "Now" in the display zone. */
export const nowInDisplayZone = (): Dayjs => dayjs().tz(current);

/** A zoneless wall-clock string ("2026-10-29" or "2026-10-29 10:00") read as display-zone wall time. */
export const wallTimeInDisplayZone = (value: string): Dayjs => dayjs.tz(value, current);
